/** Which tree paths a scoped editor may change (specs 129, 130). Pure, so the Code panel can use it too. */
import picomatch from 'picomatch/posix';
// The matcher is shared with the assistant runner, which cannot import from src/.
import { canEditPath, normalizeTreePath } from '../../packages/sandbox-runner/src/editPaths';

export { canEditPath, normalizeTreePath };
export {
	canReadPath,
	canWritePath,
	describeWriteScope,
	isKnowledgePath,
	isKnowledgePattern,
	isSubagentFile,
	KNOWLEDGE_GLOBS,
	splitFrontmatter,
	writeRefusal,
	writeRefusalMessage,
	type WriteRefusal,
	type WriteScope,
} from '../../packages/sandbox-runner/src/editPaths';

/** Patterns that would grant the whole tree, which is what `null` already means. */
const WHOLE_TREE = new Set(['**', '**/*', './**']);

const MAX_EDIT_PATHS = 200;
const MAX_PATTERN_CHARS = 300;

/** Paths in `paths` that `editPaths` does not cover. */
export function pathsOutsideScope(editPaths: readonly string[] | null | undefined, paths: Iterable<string>): string[] {
	if (editPaths == null) return [];
	return [...new Set(paths)].filter((path) => !canEditPath(editPaths, path));
}

/** Paths added, changed or deleted between two trees. A rename shows as the old path and the new one. */
export function changedTreePaths(before: Record<string, string>, after: Record<string, string>): string[] {
	const paths = new Set([...Object.keys(before), ...Object.keys(after)]);
	return [...paths].filter((path) => before[path] !== after[path]).sort();
}

/** Checks a grant before it is saved. `null` clears the limit. */
export function validateEditPaths(value: unknown): { ok: true; editPaths: string[] | null } | { ok: false; error: string } {
	if (value === null) return { ok: true, editPaths: null };
	if (!Array.isArray(value)) return { ok: false, error: 'editPaths must be an array of paths or null' };
	if (value.length > MAX_EDIT_PATHS) return { ok: false, error: `editPaths can hold at most ${MAX_EDIT_PATHS} entries` };
	const editPaths: string[] = [];
	for (const raw of value) {
		if (typeof raw !== 'string' || !raw.trim()) return { ok: false, error: 'Each edit path must be a non-empty string' };
		if (raw.length > MAX_PATTERN_CHARS) return { ok: false, error: `Edit path is too long: ${raw.slice(0, 40)}...` };
		const pattern = normalizeTreePath(raw.trim());
		if (!pattern) return { ok: false, error: `Not a path inside the agent's files: ${raw}` };
		if (WHOLE_TREE.has(pattern)) return { ok: false, error: `"${raw}" matches every file. Clear the limit instead.` };
		try {
			picomatch.makeRe(pattern);
		} catch {
			return { ok: false, error: `Not a valid pattern: ${raw}` };
		}
		if (!editPaths.includes(pattern)) editPaths.push(pattern);
	}
	return { ok: true, editPaths };
}
